import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import '../styles/About.css';

const approachSteps = [
  { title: 'Understand the problem', description: 'Start with the business objective, current workflow, and constraints that the technology needs to address.' },
  { title: 'Design the right approach', description: 'Choose a practical technical direction based on the problem instead of forcing a generic solution.' },
  { title: 'Build and integrate', description: 'Develop the system and connect the platforms, data, or payment workflows it depends on.' },
  { title: 'Improve and evolve', description: 'Refine what has been built as business requirements, integrations, and opportunities change.' },
];

const buildAreas = [
  { label: 'Capabilities', title: 'Services', description: 'The development, commerce, integration, and automation capabilities clients engage us to deliver.', path: '/services', linkLabel: 'Explore Services' },
  { label: 'Focused outcomes', title: 'Solutions', description: 'Specific technical answers designed around a defined business, workflow, or integration problem.', path: '/solutions', linkLabel: 'Explore Solutions' },
  { label: 'Bularis-built software', title: 'Products & Apps', description: 'Software products created by Bularis Tech to provide practical controls and reusable functionality.', path: '/products', linkLabel: 'Explore Products & Apps' },
];

const About = () => {
  return (
    <main className="about-page">
      <SEO
        title="About Bularis Tech | Software & Digital Solutions"
        description="Learn how Bularis Tech combines software development, e-commerce, Shopify, integrations, automation, and Bularis-built products."
        path="/about"
      />
      <section className="about-hero">
        <div className="about-hero-container">
          <div className="about-hero-copy">
            <span className="about-eyebrow">About Bularis Tech</span>
            <h1>Building Better Digital Systems for Businesses</h1>
            <p>We build websites, applications, e-commerce systems, integrations, automation, and software products around real business needs.</p>
          </div>
          <div className="about-system-visual" aria-hidden="true">
            <div className="about-system-core">B</div>
            <span className="about-system-node about-system-node-one">Build</span>
            <span className="about-system-node about-system-node-two">Connect</span>
            <span className="about-system-node about-system-node-three">Improve</span>
          </div>
        </div>
      </section>

      <section className="about-who">
        <div className="about-section-container about-who-layout">
          <div>
            <span className="about-eyebrow">Who we are</span>
            <h2>Technical thinking with a practical business focus</h2>
          </div>
          <div className="about-who-copy">
            <p>Bularis Tech is a development company working across software, e-commerce, Shopify, APIs, payment integrations, and emerging automation capabilities.</p>
            <p>We combine these disciplines to create useful systems that support customer experiences and help businesses operate more efficiently.</p>
            <div className="about-capabilities" aria-label="Bularis Tech capabilities">
              <span>Web &amp; software</span><span>E-commerce</span><span>Shopify</span><span>APIs &amp; payments</span><span>AI &amp; automation</span><span>Products &amp; apps</span>
            </div>
          </div>
        </div>
      </section>

      <section className="about-approach">
        <div className="about-section-container">
          <header className="about-section-heading">
            <span className="about-eyebrow">How we work</span>
            <h2>A straightforward path from problem to working system</h2>
            <p>Our approach stays focused on the outcome while keeping technical decisions connected to business requirements.</p>
          </header>
          <div className="about-steps">
            {approachSteps.map((step, index) => (
              <article className="about-step" key={step.title}>
                <span>{String(index + 1).padStart(2, '0')}</span><h3>{step.title}</h3><p>{step.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="about-build">
        <div className="about-section-container">
          <header className="about-section-heading">
            <span className="about-eyebrow">What we build</span>
            <h2>Different ways to solve different kinds of problems</h2>
          </header>
          <div className="about-build-grid">
            {buildAreas.map((area) => (
              <article className="about-build-card" key={area.title}>
                <span>{area.label}</span><h3>{area.title}</h3><p>{area.description}</p>
                <Link to={area.path}>{area.linkLabel} <span aria-hidden="true">→</span></Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="about-future">
        <div className="about-section-container about-future-layout">
          <div><span className="about-eyebrow">Where we’re going</span><h2>From digital development toward more intelligent workflows</h2></div>
          <div>
            <p>Bularis Tech is expanding its focus toward business process automation, AI-assisted workflows, intelligent integrations, and software products that reduce repetitive manual work.</p>
            <p>The direction is grounded in practical adoption: applying these capabilities where they can make an existing process clearer, faster, or easier to manage.</p>
          </div>
        </div>
      </section>

      <section className="about-cta">
        <div><span>Let’s build something useful</span><h2>Have a project or process you want to improve?</h2><Link to="/contact">Discuss Your Project</Link></div>
      </section>
    </main>
  );
};

export default About;
